/**
 * AccountPageSkeleton — Composable skeleton loaders for account pages
 *
 * WHY: Every account page was showing a bare spinner (ContentOnlyLoadingFallback)
 * during initial data fetch. This caused a blank-page flash before content appeared.
 * Skeleton loaders preserve layout structure during loading, preventing CLS and
 * giving users visual feedback that content is incoming.
 *
 * PATTERN: Composable building blocks (SkeletonCard, SkeletonFieldRow, etc.)
 * assembled into page-specific presets. One file avoids 8+ separate skeleton files
 * while keeping each page's skeleton accurate to its real layout.
 *
 * COUPLING: Layout dimensions (max-widths, padding, gaps) intentionally mirror
 * the real page styled components. If a page layout changes, update its skeleton
 * preset here too — otherwise users see a layout shift on transition.
 *
 * @see CheckoutSkeleton.tsx — established skeleton pattern (dark theme, MUI Skeleton)
 * @see ProfileOverview.tsx, Subscriptions.tsx, etc. — real layouts these mirror
 */

import Skeleton from "@mui/material/Skeleton";
import styled from "styled-components";

// ─── Dark-theme skeleton tokens (matches CheckoutSkeleton) ───
const SKELETON_BG = "rgba(255, 255, 255, 0.12)";

// WHY renamed: "sx" collides with MUI's ubiquitous sx prop name, causing confusion
const skeletonSx = { bgcolor: SKELETON_BG };

// ─── Shared building blocks ───

const Card = styled.div`
  background: #16181e;
  border-radius: 8px;
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 16px;
  }
`;

const BorderedCard = styled(Card)`
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 24px;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    padding: 16px;
    gap: 12px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
`;

const FieldLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 10px;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
`;

// ─── Inline-style replacements (extracted for styled-components consistency) ───

const FlexColumnGap8 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FlexColumnGap4 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FlexColumnGap6 = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FlexRowCenter8 = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FlexRowCenter12 = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FlexGrow = styled.div`
  flex: 1;
`;

const CommsCard = styled(Card)`
  gap: 24px;
`;

// ─── Composable skeleton primitives ───

function SkeletonTitle(): JSX.Element {
  return (
    <Skeleton
      variant="text"

      width={160}
      height={28}
      sx={skeletonSx}
    />
  );
}

function SkeletonEditButton(): JSX.Element {
  return (
    <Skeleton
      variant="rounded"

      width={110}
      height={32}
      sx={skeletonSx}
    />
  );
}

function SkeletonFieldRow({
  labelWidth = 100,
  valueWidth = 160,
}: {
  labelWidth?: number;
  valueWidth?: number;
}): JSX.Element {
  return (
    <FieldRow>
      <FieldLabel>
        <Skeleton
          variant="text"
    
          width={labelWidth}
          height={19}
          sx={skeletonSx}
        />
      </FieldLabel>
      <Skeleton
        variant="text"
  
        width={valueWidth}
        height={19}
        sx={skeletonSx}
      />
    </FieldRow>
  );
}

function SkeletonActionButton({ width = 120 }: { width?: number }): JSX.Element {
  return (
    <Skeleton
      variant="rounded"

      width={width}
      height={32}
      sx={skeletonSx}
    />
  );
}

function SkeletonSearchBar(): JSX.Element {
  return (
    <Skeleton
      variant="rounded"

      width="100%"
      height={40}
      sx={skeletonSx}
    />
  );
}

// ─── Page-level skeleton presets ───

// ProfileOverview: 3 cards (profile 4 fields, billing 4 fields, comms 2 fields) + actions
// Mirrors: src/features/account/ProfileOverview.tsx layout
const ProfileOverviewContainer = styled.div`
  width: 100%;
  max-width: 614px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

export function ProfileOverviewSkeleton(): JSX.Element {
  return (
    <ProfileOverviewContainer role="status" aria-label="Loading profile overview">
      {/* Page title */}
      <SectionGroup>
        <SkeletonTitle />
      </SectionGroup>

      {/* Profile card */}
      <SectionGroup>
        <Card>
          <CardHeader>
            <SkeletonEditButton />
          </CardHeader>
          <FieldList>
            <SkeletonFieldRow labelWidth={50} valueWidth={180} />
            <SkeletonFieldRow labelWidth={50} valueWidth={200} />
            <SkeletonFieldRow labelWidth={70} valueWidth={140} />
          </FieldList>
        </Card>
      </SectionGroup>

      {/* Billing details card */}
      <SectionGroup>
        <Card>
          <CardHeader>
            <SkeletonEditButton />
          </CardHeader>
          <FieldList>
            <SkeletonFieldRow labelWidth={100} valueWidth={200} />
            <SkeletonFieldRow labelWidth={40} valueWidth={140} />
            <SkeletonFieldRow labelWidth={70} valueWidth={100} />
            <SkeletonFieldRow labelWidth={60} valueWidth={80} />
          </FieldList>
        </Card>
      </SectionGroup>

      {/* Communication preferences card */}
      <SectionGroup>
        <Card>
          <CardHeader>
            <SkeletonEditButton />
          </CardHeader>
          <FieldList>
            <SkeletonFieldRow labelWidth={130} valueWidth={70} />
            <SkeletonFieldRow labelWidth={120} valueWidth={70} />
          </FieldList>
        </Card>
      </SectionGroup>

      {/* Action buttons */}
      <ActionsRow>
        <SkeletonActionButton width={100} />
        <SkeletonActionButton width={250} />
        <SkeletonActionButton width={130} />
      </ActionsRow>
    </ProfileOverviewContainer>
  );
}

// Subscriptions: title + search/filter row + 3 subscription cards
// Mirrors: src/features/account/Subscriptions.tsx layout
const SubscriptionsContainer = styled.div`
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

const SearchFilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;

  @media screen and (max-width: 767px) {
    flex-direction: column;
  }
`;

const CardHeaderSplit = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const CardTitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const CardRightArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
`;

const CardDivider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin: 0;
`;

function SubscriptionCardPlaceholder(): JSX.Element {
  return (
    <BorderedCard>
      {/* Header: title + price/badge */}
      <CardHeaderSplit>
        <CardTitleArea>
          <Skeleton variant="text" width={200} height={21} sx={skeletonSx} />
          <Skeleton variant="text" width={160} height={19} sx={skeletonSx} />
        </CardTitleArea>
        <CardRightArea>
          <Skeleton variant="text" width={80} height={21} sx={skeletonSx} />
          <Skeleton variant="rounded" width={60} height={22} sx={skeletonSx} />
        </CardRightArea>
      </CardHeaderSplit>

      <CardDivider />

      {/* Payment method row */}
      <FlexColumnGap8>
        <Skeleton variant="text" width={100} height={19} sx={skeletonSx} />
        <FlexRowCenter8>
          <Skeleton variant="rounded" width={32} height={20} sx={skeletonSx} />
          <Skeleton variant="text" width={120} height={19} sx={skeletonSx} />
        </FlexRowCenter8>
      </FlexColumnGap8>

      {/* Action button */}
      <SkeletonActionButton width={140} />
    </BorderedCard>
  );
}

export function SubscriptionsSkeleton(): JSX.Element {
  return (
    <SubscriptionsContainer role="status" aria-label="Loading subscriptions">
      <SkeletonTitle />

      {/* Search + filter */}
      <SearchFilterRow>
        <FlexGrow>
          <SkeletonSearchBar />
        </FlexGrow>
        <Skeleton variant="rounded" width={150} height={40} sx={skeletonSx} />
      </SearchFilterRow>

      {/* Results info */}
      <Skeleton variant="text" width={180} height={19} sx={skeletonSx} />

      {/* Subscription cards */}
      <SubscriptionCardPlaceholder />
      <SubscriptionCardPlaceholder />
      <SubscriptionCardPlaceholder />
    </SubscriptionsContainer>
  );
}

// Purchases: title + search/filter + purchase cards
// Mirrors: src/features/account/Purchases.tsx layout
const PurchasesContainer = styled.div`
  width: 100%;
  max-width: 614px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

function PurchaseCardPlaceholder(): JSX.Element {
  return (
    <BorderedCard>
      <CardHeaderSplit>
        <CardTitleArea>
          <Skeleton variant="text" width={180} height={21} sx={skeletonSx} />
          <Skeleton variant="text" width={140} height={19} sx={skeletonSx} />
        </CardTitleArea>
        <CardRightArea>
          <Skeleton variant="text" width={70} height={21} sx={skeletonSx} />
          <Skeleton variant="rounded" width={60} height={22} sx={skeletonSx} />
        </CardRightArea>
      </CardHeaderSplit>
    </BorderedCard>
  );
}

export function PurchasesSkeleton(): JSX.Element {
  return (
    <PurchasesContainer role="status" aria-label="Loading purchases">
      <SkeletonTitle />

      {/* Search + filter */}
      <SearchFilterRow>
        <FlexGrow>
          <SkeletonSearchBar />
        </FlexGrow>
        <Skeleton variant="rounded" width={150} height={40} sx={skeletonSx} />
      </SearchFilterRow>

      {/* Results info */}
      <Skeleton variant="text" width={140} height={19} sx={skeletonSx} />

      {/* Purchase cards */}
      <PurchaseCardPlaceholder />
      <PurchaseCardPlaceholder />
      <PurchaseCardPlaceholder />
    </PurchasesContainer>
  );
}

// BillingOverview: title + search + table header + rows
// Mirrors: src/features/account/BillingOverview.tsx layout
const BillingContainer = styled.div`
  width: 100%;
  max-width: 960px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 110px 100px 40px;
  gap: 12px;
  padding: 12px 16px;
  background: #16181e;
  border-radius: 8px 8px 0 0;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr 110px 100px 40px;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
    background: #16181e;
    border-radius: 8px;
    border-bottom: none;
  }
`;

function SkeletonTableHeaderRow(): JSX.Element {
  return (
    <TableHeader>
      <Skeleton variant="text" width={50} height={16} sx={skeletonSx} />
      <Skeleton variant="text" width={90} height={16} sx={skeletonSx} />
      <Skeleton variant="text" width={60} height={16} sx={skeletonSx} />
      <Skeleton variant="text" width={50} height={16} sx={skeletonSx} />
      <div />
    </TableHeader>
  );
}

function SkeletonTableRow(): JSX.Element {
  return (
    <TableRow>
      <Skeleton variant="text" width={80} height={19} sx={skeletonSx} />
      <Skeleton variant="text" width="80%" height={19} sx={skeletonSx} />
      <Skeleton variant="text" width={80} height={19} sx={skeletonSx} />
      <Skeleton variant="text" width={60} height={19} sx={skeletonSx} />
      <Skeleton variant="circular" width={24} height={24} sx={skeletonSx} />
    </TableRow>
  );
}

export function BillingOverviewSkeleton(): JSX.Element {
  return (
    <BillingContainer role="status" aria-label="Loading billing history">
      <SkeletonTitle />

      {/* Search bar */}
      <SkeletonSearchBar />

      {/* Table */}
      <div>
        <SkeletonTableHeaderRow />
        <SkeletonTableRow />
        <SkeletonTableRow />
        <SkeletonTableRow />
        <SkeletonTableRow />
        <SkeletonTableRow />
      </div>
    </BillingContainer>
  );
}

// PaymentMethods: title + payment method cards + add button
// Mirrors: src/features/account/PaymentMethods.tsx layout
const PaymentMethodsContainer = styled.div`
  width: 100%;
  max-width: 614px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

const PaymentMethodCardSkeleton = styled.div`
  background: #16181e;
  border-radius: 12px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  width: 100%;

  @media screen and (max-width: 767px) {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CardLeftArea = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

function PaymentMethodPlaceholder(): JSX.Element {
  return (
    <PaymentMethodCardSkeleton>
      <CardLeftArea>
        {/* Card brand icon */}
        <Skeleton variant="rounded" width={48} height={32} sx={skeletonSx} />
        {/* Card details */}
        <FlexColumnGap4>
          <Skeleton variant="text" width={140} height={19} sx={skeletonSx} />
          <Skeleton variant="text" width={100} height={16} sx={skeletonSx} />
        </FlexColumnGap4>
      </CardLeftArea>
      {/* Currency badge + remove button */}
      <FlexRowCenter12>
        <Skeleton variant="rounded" width={40} height={24} sx={skeletonSx} />
        <Skeleton variant="rounded" width={80} height={32} sx={skeletonSx} />
      </FlexRowCenter12>
    </PaymentMethodCardSkeleton>
  );
}

export function PaymentMethodsSkeleton(): JSX.Element {
  return (
    <PaymentMethodsContainer role="status" aria-label="Loading payment methods">
      <SkeletonTitle />
      <PaymentMethodPlaceholder />
      <PaymentMethodPlaceholder />
      {/* Add payment method button */}
      <SkeletonActionButton width={180} />
    </PaymentMethodsContainer>
  );
}

// AddOrUpdatePaymentMethod: title + form card (Stripe PaymentElement placeholder) + info line
// Mirrors: src/features/account/AddOrUpdatePaymentMethod.tsx layout
// WHY: While the SetupIntent + Stripe publishable key are fetched, the page previously
// rendered <ContentOnlyLoadingFallback /> (a bare spinner). This was missed in the
// DEV-2099 skeleton rollout. Showing a skeleton card matches the rest of the account
// loading experience and prevents the spinner-then-form layout shift.
const AddPaymentMethodContainer = styled.div`
  width: 100%;
  max-width: 614px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

const AddPaymentFormCard = styled.div`
  background: #16181e;
  border-radius: 8px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media screen and (max-width: 767px) {
    padding: 24px 16px;
    gap: 20px;
  }
`;

const AddPaymentButtonRow = styled.div`
  display: flex;
  gap: 12px;

  @media screen and (max-width: 767px) {
    flex-direction: column-reverse;
  }
`;

const AddPaymentInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export function AddPaymentMethodSkeleton(): JSX.Element {
  return (
    <AddPaymentMethodContainer role="status" aria-label="Loading payment form">
      {/* Page title */}
      <SkeletonTitle />

      <AddPaymentFormCard>
        {/* Stripe PaymentElement placeholder: card number, expiry + cvc row, billing fields.
            Heights roughly match the real Stripe inputs (44px) to minimise layout shift. */}
        <SkeletonFormField />

        <FormFieldRow>
          <SkeletonFormField />
          <SkeletonFormField />
        </FormFieldRow>

        <SkeletonFormField />

        {/* Cancel + submit buttons (flex:1 each in real layout) */}
        <AddPaymentButtonRow>
          <Skeleton variant="rounded" width="100%" height={44} sx={skeletonSx} />
          <Skeleton variant="rounded" width="100%" height={44} sx={skeletonSx} />
        </AddPaymentButtonRow>

        {/* "Your payment information is securely processed by Stripe." info line */}
        <AddPaymentInfoRow>
          <Skeleton variant="circular" width={20} height={20} sx={skeletonSx} />
          <Skeleton variant="text" width={280} height={16} sx={skeletonSx} />
        </AddPaymentInfoRow>
      </AddPaymentFormCard>
    </AddPaymentMethodContainer>
  );
}

// ProfileEdit: title + subtitle + form fields
// Mirrors: src/features/account/ProfileEdit.tsx layout
const EditFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 700px;

  @media screen and (max-width: 767px) {
    gap: 24px;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormFieldsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media screen and (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

function SkeletonFormField({ width = "100%" }: { width?: string | number }): JSX.Element {
  return (
    <FlexColumnGap6>
      <Skeleton variant="text" width={80} height={16} sx={skeletonSx} />
      <Skeleton variant="rounded" width={width} height={44} sx={skeletonSx} />
    </FlexColumnGap6>
  );
}

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

export function ProfileEditSkeleton(): JSX.Element {
  return (
    <EditFormContainer role="status" aria-label="Loading profile editor">
      <FormSection>
        <SkeletonTitle />
        <Skeleton variant="text" width={280} height={20} sx={skeletonSx} />
      </FormSection>

      <FormFieldsColumn>
        {/* Name + Surname row */}
        <FormFieldRow>
          <SkeletonFormField />
          <SkeletonFormField />
        </FormFieldRow>

        {/* Phone field */}
        <SkeletonFormField />

        {/* Password section */}
        <FormSection>
          <Skeleton variant="text" width={120} height={20} sx={skeletonSx} />
          <Skeleton variant="text" width={250} height={16} sx={skeletonSx} />
          <SkeletonActionButton width={160} />
        </FormSection>
      </FormFieldsColumn>

      {/* Buttons */}
      <ButtonGroup>
        <SkeletonActionButton width={100} />
        <SkeletonActionButton width={120} />
      </ButtonGroup>
    </EditFormContainer>
  );
}

// BillingEdit: title + subtitle + Stripe address element placeholder
// Mirrors: src/features/account/BillingEdit.tsx layout
export function BillingEditSkeleton(): JSX.Element {
  return (
    <EditFormContainer role="status" aria-label="Loading billing editor">
      <FormSection>
        <SkeletonTitle />
        <Skeleton variant="text" width={320} height={20} sx={skeletonSx} />
      </FormSection>

      <FormFieldsColumn>
        {/* Name field */}
        <SkeletonFormField />

        {/* Address line 1 */}
        <SkeletonFormField />

        {/* Address line 2 */}
        <SkeletonFormField />

        {/* City + State row */}
        <FormFieldRow>
          <SkeletonFormField />
          <SkeletonFormField />
        </FormFieldRow>

        {/* Postcode + Country row */}
        <FormFieldRow>
          <SkeletonFormField />
          <SkeletonFormField />
        </FormFieldRow>
      </FormFieldsColumn>

      {/* Buttons */}
      <ButtonGroup>
        <SkeletonActionButton width={100} />
        <SkeletonActionButton width={120} />
      </ButtonGroup>
    </EditFormContainer>
  );
}

// CommunicationPreferencesEdit: title + card with toggle switches
// Mirrors: src/features/account/CommunicationPreferencesEdit.tsx layout
const CommsContainer = styled.div`
  width: 100%;
  max-width: 614px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 0;

  @media screen and (max-width: 767px) {
    gap: 16px;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export function CommunicationPreferencesSkeleton(): JSX.Element {
  return (
    <CommsContainer role="status" aria-label="Loading communication preferences">
      <SkeletonTitle />

      <CommsCard>
        {/* Email notifications toggle */}
        <ToggleRow>
          <FlexColumnGap4>
            <Skeleton variant="text" width={150} height={19} sx={skeletonSx} />
            <Skeleton variant="text" width={220} height={16} sx={skeletonSx} />
          </FlexColumnGap4>
          <Skeleton variant="rounded" width={42} height={24} sx={skeletonSx} />
        </ToggleRow>

        {/* SMS notifications toggle */}
        <ToggleRow>
          <FlexColumnGap4>
            <Skeleton variant="text" width={140} height={19} sx={skeletonSx} />
            <Skeleton variant="text" width={200} height={16} sx={skeletonSx} />
          </FlexColumnGap4>
          <Skeleton variant="rounded" width={42} height={24} sx={skeletonSx} />
        </ToggleRow>
      </CommsCard>

      {/* Buttons */}
      <ButtonGroup>
        <SkeletonActionButton width={100} />
        <SkeletonActionButton width={120} />
      </ButtonGroup>
    </CommsContainer>
  );
}
